import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet'
import L from 'leaflet'
import {
  MapPinIcon,
  TruckIcon,
  UserIcon,
  PhoneIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  BuildingOfficeIcon,
  AcademicCapIcon,
  SignalIcon,
  SignalSlashIcon
} from '@heroicons/react/24/outline'
import { getTrackingByToken, type TrackingByTokenResponse } from '@/api/tripsApi'
import 'leaflet/dist/leaflet.css'

// Fix Leaflet default marker icons
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'

delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
})

// Custom icons
const createIcon = (color: string, emoji: string) => L.divIcon({
  html: `<div style="background:${color};width:36px;height:36px;border-radius:50%;display:flex;align-items:center;justify-content:center;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);font-size:16px;">${emoji}</div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 18],
  popupAnchor: [0, -20],
  className: ''
})

const startIcon = createIcon('#10B981', '🏠')
const endIcon = createIcon('#2563EB', '🏫')
const busIcon = createIcon('#F59E0B', '🚌')

// Auto-fit map bounds component
const FitBounds: React.FC<{ points: [number, number][] }> = ({ points }) => {
  const map = useMap()
  useEffect(() => {
    if (points.length > 0) {
      const bounds = L.latLngBounds(points.map(p => L.latLng(p[0], p[1])))
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 15 })
    }
  }, [points, map])
  return null
}

const StudentTrackingPage: React.FC = () => {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const [data, setData] = useState<TrackingByTokenResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [errorCode, setErrorCode] = useState<string | null>(null)
  const [wsConnected, setWsConnected] = useState(false)
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Build WebSocket URL from the current API base
  const getWsUrl = useCallback(() => {
    const apiBase = (import.meta as any).env.VITE_API_BASE_URL || 'http://localhost:5003/api/v1'
    const wsBase = apiBase.replace(/^http/, 'ws').replace(/\/api\/v1$/, '')
    const jwtToken = localStorage.getItem('accessToken')
    return `${wsBase}/api/v1/tracking/${token}?token=${jwtToken}`
  }, [token])

  // Connect WebSocket for real-time updates
  const connectWebSocket = useCallback(() => {
    if (!token || !data) return
    if (data.trackingSession.status !== 'active') return

    const wsUrl = getWsUrl()
    const ws = new WebSocket(wsUrl)

    ws.onopen = () => {
      setWsConnected(true)
      console.log('Tracking WebSocket connected')
    }

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data)

        if (msg.type === 'location_update' && msg.data) {
          // Update the vehicle's current location on the map
          setData(prev => prev ? {
            ...prev,
            currentLocation: {
              latitude: msg.data.latitude,
              longitude: msg.data.longitude,
              address: msg.data.address || prev.currentLocation?.address || '',
              lastUpdated: msg.timestamp,
            }
          } : prev)
        } else if (msg.type === 'student_dropped_off' || msg.type === 'trip_completed') {
          // Update session status to completed
          setData(prev => prev ? {
            ...prev,
            trackingSession: {
              ...prev.trackingSession,
              status: 'completed',
              completedAt: msg.timestamp,
            }
          } : prev)
        } else if (msg.type === 'student_boarded' && msg.data) {
          // Update journey data if it comes through
          setData(prev => prev ? {
            ...prev,
            journey: {
              start: msg.data.journeyStart || prev.journey.start,
              end: msg.data.journeyEnd || prev.journey.end,
            }
          } : prev)
        }
      } catch {
        // Ignore malformed messages
      }
    }

    ws.onclose = (event) => {
      setWsConnected(false)
      wsRef.current = null
      // Auto-reconnect if not a deliberate close and session is still active
      if (event.code !== 1000 && event.code < 4000) {
        reconnectTimeoutRef.current = setTimeout(connectWebSocket, 3000)
      }
    }

    ws.onerror = () => {
      setWsConnected(false)
    }

    wsRef.current = ws
  }, [token, data?.trackingSession?.status, getWsUrl])

  // Initial data fetch via REST
  useEffect(() => {
    const fetchInitialData = async () => {
      if (!token) return
      try {
        const result = await getTrackingByToken(token)
        setData(result)
        setError(null)
        setErrorCode(null)
      } catch (err: any) {
        const status = err.response?.status
        const code = err.response?.data?.code
        const message = err.response?.data?.message

        if (status === 401) {
          setError('Please log in to the SmartTag School app to view tracking.')
          setErrorCode(code || 'AUTH_REQUIRED')
        } else if (status === 403) {
          setError('You are not authorized to view this tracking session.')
          setErrorCode('FORBIDDEN')
        } else if (status === 410) {
          setError('This tracking session has expired.')
          setErrorCode('EXPIRED')
        } else if (status === 404) {
          setError('Tracking session not found.')
          setErrorCode('NOT_FOUND')
        } else {
          setError(message || 'Failed to load tracking data.')
          setErrorCode('ERROR')
        }
      } finally {
        setLoading(false)
      }
    }
    fetchInitialData()
  }, [token])

  // Open WebSocket after initial data is loaded and session is active
  useEffect(() => {
    if (data && data.trackingSession.status === 'active') {
      connectWebSocket()
    }
    return () => {
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounting')
        wsRef.current = null
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
      }
    }
  }, [data?.trackingSession?.status === 'active' ? 'active' : 'inactive'])

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="h-12 w-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-gray-600">Loading tracking data...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <ExclamationTriangleIcon className="h-16 w-16 text-amber-500 mx-auto" />
          <h2 className="mt-4 text-xl font-bold text-gray-900">
            {errorCode === 'EXPIRED' ? 'Session Expired' :
             errorCode === 'AUTH_REQUIRED' ? 'Login Required' :
             'Tracking Unavailable'}
          </h2>
          <p className="mt-2 text-gray-600">{error}</p>
          {errorCode === 'AUTH_REQUIRED' && (
            <button
              onClick={() => navigate('/login')}
              className="mt-6 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go to Login
            </button>
          )}
        </div>
      </div>
    )
  }

  if (!data) return null

  const { trackingSession, currentLocation, journey } = data
  const isActive = trackingSession.status === 'active'

  // Helper to validate a coordinate point has valid lat/lng
  const isValidCoord = (point: { latitude?: number; longitude?: number } | null | undefined): point is { latitude: number; longitude: number } => {
    return !!(point && typeof point.latitude === 'number' && typeof point.longitude === 'number' && !isNaN(point.latitude) && !isNaN(point.longitude))
  }

  const hasStart = isValidCoord(journey?.start)
  const hasEnd = isValidCoord(journey?.end)
  const hasVehicle = isValidCoord(currentLocation)

  // Collect all map points for bounds fitting
  const mapPoints: [number, number][] = []
  if (hasStart) mapPoints.push([journey.start!.latitude, journey.start!.longitude])
  if (hasEnd) mapPoints.push([journey.end!.latitude, journey.end!.longitude])
  if (hasVehicle) mapPoints.push([currentLocation!.latitude, currentLocation!.longitude])

  // Default center if no points
  const defaultCenter: [number, number] = mapPoints.length > 0
    ? mapPoints[0]
    : [-1.2921, 36.8219] // Nairobi fallback

  // Route line between valid points only
  const routeLine: [number, number][] = []
  if (hasStart) routeLine.push([journey.start!.latitude, journey.start!.longitude])
  if (hasVehicle) routeLine.push([currentLocation!.latitude, currentLocation!.longitude])
  if (hasEnd) routeLine.push([journey.end!.latitude, journey.end!.longitude])

  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              isActive ? 'bg-green-100' : 'bg-gray-100'
            }`}>
              <TruckIcon className={`h-5 w-5 ${isActive ? 'text-green-600' : 'text-gray-400'}`} />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">
                {trackingSession.studentInfo.firstName}'s Trip
              </h1>
              <p className="text-xs text-gray-500">
                {trackingSession.tripInfo.tripName}
                {trackingSession.tripInfo.routeName && ` • ${trackingSession.tripInfo.routeName}`}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {isActive && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 animate-pulse">
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1" />
                Live
              </span>
            )}
            {!isActive && (
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                <CheckCircleIcon className="h-3.5 w-3.5 mr-1" />
                Completed
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="relative" style={{ height: '50vh', minHeight: '300px' }}>
        {mapPoints.length > 0 ? (
          <MapContainer
            center={defaultCenter}
            zoom={13}
            style={{ height: '100%', width: '100%' }}
            zoomControl={false}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitBounds points={mapPoints} />

            {/* Journey start marker */}
            {hasStart && (
              <Marker
                position={[journey.start!.latitude, journey.start!.longitude]}
                icon={startIcon}
              >
                <Popup>
                  <div className="text-sm">
                    <p className="font-semibold">Start: {journey.start!.name || 'Pickup Point'}</p>
                    <p className="text-gray-500 text-xs">
                      {journey.start!.latitude.toFixed(5)}, {journey.start!.longitude.toFixed(5)}
                    </p>
                  </div>
                </Popup>
              </Marker>
            )}

            {/* Journey end marker */}
            {hasEnd && (
              <Marker
                position={[journey.end!.latitude, journey.end!.longitude]}
                icon={endIcon}
              >
                <Popup>
                  <div className="text-sm">
                    <p className="font-semibold">Destination: {journey.end!.name || 'Drop-off Point'}</p>
                    <p className="text-gray-500 text-xs">
                      {journey.end!.latitude.toFixed(5)}, {journey.end!.longitude.toFixed(5)}
                    </p>
                  </div>
                </Popup>
              </Marker>
            )}

            {/* Live vehicle position */}
            {hasVehicle && (
              <Marker
                position={[currentLocation!.latitude, currentLocation!.longitude]}
                icon={busIcon}
              >
                <Popup>
                  <div className="text-sm">
                    <p className="font-semibold">Vehicle: {trackingSession.vehicleInfo.vehicleNumber}</p>
                    <p className="text-gray-500 text-xs">
                      Updated: {currentLocation!.lastUpdated
                        ? new Date(currentLocation!.lastUpdated).toLocaleTimeString()
                        : 'Just now'}
                    </p>
                  </div>
                </Popup>
              </Marker>
            )}

            {/* Route line */}
            {routeLine.length >= 2 && (
              <Polyline
                positions={routeLine}
                pathOptions={{ color: '#2563EB', weight: 3, dashArray: '8 4', opacity: 0.7 }}
              />
            )}
          </MapContainer>
        ) : (
          <div className="h-full bg-gray-100 flex items-center justify-center">
            <div className="text-center text-gray-500">
              <MapPinIcon className="h-12 w-12 mx-auto text-gray-300" />
              <p className="mt-2">No location data available yet</p>
            </div>
          </div>
        )}

        {/* WebSocket connection status */}
        {isActive && (
          <div className="absolute bottom-3 right-3 z-[1000]">
            <div className={`rounded-full px-3 py-1.5 shadow-md text-xs flex items-center space-x-1.5 ${
              wsConnected ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
            }`}>
              {wsConnected ? (
                <>
                  <SignalIcon className="h-3.5 w-3.5" />
                  <span>Live</span>
                </>
              ) : (
                <>
                  <SignalSlashIcon className="h-3.5 w-3.5" />
                  <span>Reconnecting...</span>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Info Cards */}
      <div className="max-w-4xl mx-auto px-4 py-4 space-y-3">
        {/* Student & Trip Info */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-start space-x-3">
              <UserIcon className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Student</p>
                <p className="text-sm font-semibold text-gray-900">
                  {trackingSession.studentInfo.firstName} {trackingSession.studentInfo.lastName}
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <ClockIcon className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">
                  {trackingSession.checkInType === 'boarded' ? 'Boarded at' : 'Dropped off at'}
                </p>
                <p className="text-sm font-semibold text-gray-900">
                  {formatTime(trackingSession.startedAt)}
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <MapPinIcon className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Stop</p>
                <p className="text-sm font-semibold text-gray-900">{trackingSession.stopName}</p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <TruckIcon className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-500">Vehicle</p>
                <p className="text-sm font-semibold text-gray-900">{trackingSession.vehicleInfo.vehicleNumber}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Journey Info */}
        {(hasStart || hasEnd) && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Journey</h3>
            <div className="flex items-center space-x-3">
              {/* Start */}
              <div className="flex-1 text-center">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  {trackingSession.tripInfo.tripType === 'pickup'
                    ? <AcademicCapIcon className="h-4 w-4 text-green-600" />
                    : <BuildingOfficeIcon className="h-4 w-4 text-green-600" />
                  }
                </div>
                <p className="text-xs font-medium text-gray-900 mt-1">
                  {journey.start?.name || 'Start'}
                </p>
              </div>

              {/* Arrow */}
              <div className="flex-shrink-0">
                <div className="w-16 h-0.5 bg-blue-300 relative">
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-0 h-0 border-l-[6px] border-l-blue-400 border-y-[4px] border-y-transparent" />
                  {isActive && (
                    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                      <span className="text-sm">🚌</span>
                    </div>
                  )}
                </div>
              </div>

              {/* End */}
              <div className="flex-1 text-center">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                  {trackingSession.tripInfo.tripType === 'pickup'
                    ? <BuildingOfficeIcon className="h-4 w-4 text-blue-600" />
                    : <AcademicCapIcon className="h-4 w-4 text-blue-600" />
                  }
                </div>
                <p className="text-xs font-medium text-gray-900 mt-1">
                  {journey.end?.name || 'Destination'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Driver Info */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                <UserIcon className="h-5 w-5 text-gray-500" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Driver</p>
                <p className="text-sm font-semibold text-gray-900">{trackingSession.driverInfo.driverName}</p>
              </div>
            </div>
            {trackingSession.driverInfo.driverPhone && (
              <a
                href={`tel:${trackingSession.driverInfo.driverPhone}`}
                className="flex items-center space-x-1 px-3 py-1.5 bg-green-50 text-green-700 rounded-lg text-sm hover:bg-green-100 transition-colors"
              >
                <PhoneIcon className="h-4 w-4" />
                <span>Call</span>
              </a>
            )}
          </div>
        </div>

        {/* Status footer */}
        {trackingSession.completedAt && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center">
            <p className="text-sm text-green-800">
              <CheckCircleIcon className="h-4 w-4 inline mr-1" />
              Trip completed at {formatTime(trackingSession.completedAt)}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default StudentTrackingPage
